<script setup lang="ts">
import SwitchToggle from "@/components/SwitchToggle/SwitchToggle.vue";
import {computed, onMounted, reactive, ref, watch,} from "vue";
import {useI18n} from "vue-i18n";
import {useMatches} from "vue-responsiveness";
import * as Calendars from "@/composables/calendar";
import {useSearchStore} from "@/stores/searchStore";
import {useRoute} from "vue-router";
import {trackEvent} from "@/services/event.service";

const {t} = useI18n(); // Initialize the i18n function
const useCalendar = reactive(Calendars.useCalendar())
const searchStore = useSearchStore();
const open = ref(false);
const route = useRoute();
const isRecommendationsPage = computed(() => route.path === "/recommendations");
const isYachtPage = computed(() => route.path.startsWith("/yachts"));

const emit = defineEmits<{
  'on-date-select': [dates?: Date[]],
}>();


const props = defineProps({
  blockedDates: {
    type: Array<Date>,
    default: []
  },
  initialTab: {
    type: String<'Dates' | 'Flexible'>,
    default: 'Dates'
  },
  warning:{
    type: String,
    default:''
  },
  showTabs: {
    type: Boolean,
    default: true,
  },
  columns: { 
    type: Number,
    default: 2,
  },
  rows: {
    type: Number,
    default: 1,
  },
  calendarClass: { 
    type: String,
    default: "scale-[0.9]"
  },
});

const matches = useMatches()
const inputWidth = computed(() => matches.isMin('min') ? 'w-full' : 'w-[16vw]');

const dateFrom = defineModel<Date>('dateFrom');
const dateTo = defineModel<Date>('dateTo');

const range = reactive({
  start: dateFrom.value,
  end: dateTo.value
});


watch([dateFrom, dateTo], ([newDateFrom, newDateTo]) => {
  useCalendar.fromDate = newDateFrom;
  useCalendar.toDate = newDateTo;
}, { deep: true });

watch(useCalendar, (newCalendar) => {
  if (newCalendar.hasDates) {
    dateFrom.value = newCalendar.fromDate;
    dateTo.value = newCalendar.toDate;
    emit('on-date-select', [newCalendar.fromDate, newCalendar.toDate]);

    // Track event here
    trackEvent('cosmosy.ga.filters.date', {
      event_label: 'Date Picker',
      selected_dates: `${newCalendar.fromDate} to ${newCalendar.toDate}`,
    });
  }


});


function onDayClick(val) {
  const date = new Date(val.date.getTime() - (val.date.getTimezoneOffset() * 60000))
  useCalendar.setDate(date)
}



const toggleOpen = () => {
  open.value = !open.value;
}

onMounted(() => {
  useCalendar.selectedOption = props.initialTab;
})


</script>

<template>
  <div class="relative flex">
    <slot name="display" :startString="useCalendar.startString" :endString="useCalendar.endString" :toggleOpen="toggleOpen">
      <VTextField variant="underlined"
                  :model-value="`${useCalendar.startString} to ${useCalendar.endString}`"
                  readonly
                  :class="inputWidth"
                  @click="toggleOpen">
        <img v-if="!isYachtPage" src="@/assets/icons/svg/Calendar_16x16.svg" :alt="t('dates')" class="min-w-5"/>
      </VTextField>
    </slot>
    <div v-if="open" v-click-outside="() => open = false"
         :class="[
       'dateinputest shadow-2xl absolute top-full h-fit left-0 bg-white flex flex-col justify-center items-center py-[20px] z-[2001] overflow-hidden',
       isRecommendationsPage ? 'w-[350px]' : 'w-full' // Set fixed width for recommendations page
     ]">
      <SwitchToggle
          v-if="!route.path.startsWith('/yachts/')"  
      :options="[t('datePicker.dates'), t('datePicker.flexible')]"
      :value="useCalendar.selectedOption"
      @update:value="val => useCalendar.selectedOption = val as 'Dates' | 'Flexible'"
      />
      <div v-if="useCalendar.selectedOption === 'Dates'" class="flex flex-col items-center justify-center">
        <VDatePicker
            expanded
            v-model.range="range"
            :model-value="[dateFrom, dateTo]"
            :attributes="useCalendar.dateRange"
            borderless
            @mouseover="onMouseOver" 
            :class="calendarClass"
            :columns="props.columns"
            :rows="props.rows"
            :min-date="new Date()"
            @dayclick="onDayClick"
            :firstDayOfWeek=2
        />
          <div class="w-3/4 responsive-body-xsmall flex items-center gap-0.5" v-if="warning && warning !=''">
              <img src="@/assets/icons/svg/Action_required_16x16.svg" :alt="t('info')"/>
              <span>{{ warning }}</span>
          </div>
      </div>
      <div v-else class="flex flex-col justify-center items-center w-full h-full">
        <div class="flex flex-col justify-center items-center mt-7.5">
          <div>{{ t('datePicker.duration') }}</div>
          <div class="flex items-center gap-1.5 mt-2 cursor-pointer">
            <VChip :variant="useCalendar.selectedFlexiblePeriod === '7 days' ? 'flat' : 'outlined'"
                   @click="useCalendar.selectedFlexiblePeriod = '7 days'; useCalendar.howManyWeeks = 1">{{ t('datePicker.7days') }}
            </VChip>
            <VChip :variant="useCalendar.selectedFlexiblePeriod === '2 weeks' ? 'flat' : 'outlined'"
                   @click="useCalendar.selectedFlexiblePeriod = '2 weeks'; useCalendar.howManyWeeks = 2">{{ t('datePicker.2weeks') }}
            </VChip>
            <VChip :variant="useCalendar.selectedFlexiblePeriod === '3 weeks' ? 'flat' : 'outlined'"
                   @click="useCalendar.selectedFlexiblePeriod = '3 weeks'; useCalendar.howManyWeeks = 3">{{ t('datePicker.3weeks') }}
            </VChip>
            <VChip :variant="useCalendar.selectedFlexiblePeriod === '1 month' ? 'flat' : 'outlined'"
                   @click="useCalendar.selectedFlexiblePeriod = '1 month'; useCalendar.howManyWeeks = 4">{{ t('datePicker.1month') }}
            </VChip>
          </div>
        </div>
        <div class="flex flex-col justify-center items-center mt-7.5 w-full h-full">
          <div>{{ t('duration') }}</div>
          <VSheet class="mt-2 w-full overflow-x-auto">
            <VSlideGroup>
              <VSlideGroupItem v-for="month in useCalendar.sortedMonths" :key="month.id">
                <div @click="useCalendar.setMonth(month)"
                     :class="`flex justify-center month items-center w-20 h-16 bg-background-light mx-1 rounded-2xl cursor-pointer
                          ${useCalendar.selectedFlexibleMonth?.id == month.id ? 'border border-primary-blue' : ''}`">
                  {{ month.name }}
                </div>
              </VSlideGroupItem>
            </VSlideGroup>
          </VSheet>
        </div>
      </div>
    </div>
  </div>
</template>


<style scoped>
:deep(.weekday-7 div) {
@apply font-bold
}

:deep(div .vc-weekday .vc-weekday-7) {
@apply font-bold
}
</style>